import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from 'context/AuthContext';
import { validateBanca, registerBanca } from 'services/auth.service';
import loginElbbImg from 'assets/img/2026/login_elbb.png';
import estrelasElbb from 'assets/img/2026/estrelas_elbb.png';
import './style.scss';

type LoginStep = 'landing' | 'email' | 'register' | 'login';

interface ValidatedUser {
  name: string;
  email: string;
  _id: string;
  hasPassword?: boolean;
  password?: string;
}

const OTP_TOTAL_SECONDS = 59;

export function LoginBanca() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<LoginStep>('landing');
  const [errorMessage, setErrorMessage] = useState('');

  const [emailInput, setEmailInput] = useState('');

  const [validatedUser, setValidatedUser] = useState<ValidatedUser | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '']);
  const [otpCountdown, setOtpCountdown] = useState(OTP_TOTAL_SECONDS);
  const [canResend, setCanResend] = useState(false);
  const [otpTimerKey, setOtpTimerKey] = useState(0);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [loginInput, setLoginInput] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Forgot password — reuses the 'register' step UI (sends OTP via banca_validate)
  const [forgotMode, setForgotMode] = useState(false);

  useEffect(() => {
    if (step !== 'register') return;
    setOtpCountdown(OTP_TOTAL_SECONDS);
    setCanResend(false);

    const timer = setInterval(() => {
      setOtpCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [step, otpTimerKey]);

  async function handleEmailSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    if (!emailInput.trim()) return;
    setLoading(true);
    setErrorMessage('');
    try {
      const email = emailInput.trim().toLowerCase();
      const result = await validateBanca({ email });
      if (result) {
        setValidatedUser(result);
        if (result.hasPassword || result.password) {
          setLoginInput(result._id || email);
          setStep('login');
        } else {
          setStep('register');
        }
      }
    } catch (e: any) {
      setErrorMessage(e?.message || 'Email não encontrado ou não autorizado.');
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPasswordBanca() {
    const email = (loginInput || emailInput).trim().toLowerCase();
    if (!email) {
      setErrorMessage('Informe o email para recuperar a senha.');
      return;
    }
    setLoading(true);
    setErrorMessage('');
    try {
      const result = await validateBanca({ email, forgot: true });
      if (result) {
        setValidatedUser(result);
        setForgotMode(true);
        setPassword('');
        setConfirmPassword('');
        setOtpDigits(['', '', '', '', '']);
        setStep('register');
      }
    } catch (e: any) {
      setErrorMessage(e?.message || 'Não foi possível enviar o código. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  async function handleResendOtp() {
    if (!canResend) return;
    const target = validatedUser?.email || emailInput.trim().toLowerCase();
    try {
      await validateBanca({ email: target, forgot: forgotMode || undefined });
    } catch {}
    setOtpDigits(['', '', '', '', '']);
    setOtpTimerKey(k => k + 1);
    setTimeout(() => otpRefs.current[0]?.focus(), 50);
  }

  function handleOtpInput(index: number, value: string) {
    if (!/^\d?$/.test(value)) return;
    const newDigits = [...otpDigits];
    newDigits[index] = value;
    setOtpDigits(newDigits);
    if (value && index < 4) {
      otpRefs.current[index + 1]?.focus();
    }
  }

  function handleOtpPaste(index: number, e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5 - index);
    if (!pasted) return;
    const newDigits = [...otpDigits];
    for (let i = 0; i < pasted.length; i++) {
      newDigits[index + i] = pasted[i];
    }
    setOtpDigits(newDigits);
    const lastIdx = Math.min(index + pasted.length - 1, 4);
    const nextIdx = Math.min(index + pasted.length, 4);
    const focusTarget = pasted.length === 5 - index ? lastIdx : nextIdx;
    otpRefs.current[focusTarget]?.focus();
  }

  function handleOtpKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  }

  async function handleRegisterSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setErrorMessage('');
    if (password !== confirmPassword) {
      setErrorMessage('As senhas não coincidem.');
      return;
    }
    const code = otpDigits.join('');
    if (code.length < 5) {
      setErrorMessage('Digite o código de 5 dígitos enviado para o seu email.');
      return;
    }
    setLoading(true);
    try {
      await registerBanca({
        _id: validatedUser!.email,
        password,
        code,
      });
      setLoginInput(validatedUser!.email);
      setLoginPassword('');
      setPassword('');
      setConfirmPassword('');
      setOtpDigits(['', '', '', '', '']);
      setErrorMessage('');
      setStep('login');
    } catch (e: any) {
      setErrorMessage(e?.message || 'Erro ao cadastrar. Verifique o código e tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  async function handleLoginSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);
    try {
      // Marca explicitamente a sessão como banca técnica para que o signOut
      // possa redirecionar para /login-banca sem depender de heurística do _id.
      try {
        window.localStorage.setItem('bb.banca-session', '1');
      } catch {
        /* ignora */
      }
      await login({ username: loginInput, password: loginPassword });
      navigate('/home-elbb');
    } catch {
      setErrorMessage('Login ou senha incorretos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  function formatCountdown(seconds: number) {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  if (step === 'landing') {
    return (
      <div id="login-public-wrapper" className="login-theme-2026">
        <div className="relative flex flex-col w-screen h-screen overflow-hidden main-background">
          <div className="absolute inset-0 z-0 stars-container" aria-hidden="true">
            <img src={estrelasElbb} alt="" className="w-full h-full object-cover" />
          </div>

          <main className="flex flex-1 items-center justify-center px-4 md:px-8 lg:px-12 relative pb-4 content-wrapper">
            <div className="flex flex-col items-center justify-center max-w-5xl z-10 w-full login-content">
              <div className="text-center mb-6 space-y-0.5 headings-container">
                <h1 className="font-bold leading-tight">
                  <span className="block">A grande jornada com iniciativas</span>
                  <span className="block">transformadoras e inspiradoras começou.</span>
                </h1>
                <p className="font-bold leading-tight mt-1 highlight-text">
                  A sua participação como <span>avaliador técnico</span> é fundamental!
                </p>
              </div>

              <button
                onClick={() => setStep('email')}
                className="btn-iniciar-jornada"
              >
                ENTRAR
              </button>

              <div className="images-container">
                <img
                  src={loginElbbImg}
                  alt="Logo Prêmio ELBB"
                  className="login-elbb__illustration"
                />
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div id="login-public-wrapper" className="login-theme-2026">
      <div className="login-form-bg flex flex-col w-screen min-h-screen px-8 py-10 md:px-16 md:py-14">
        <div className="mb-6 login-intro">
          <h1 className="text-2xl md:text-3xl font-bold leading-tight">
            <span className="text-white">Seja bem-vindo ao </span>
            <span style={{ color: '#FBF434' }}>Prêmio ELBB!</span>
          </h1>
          <p className="text-2xl md:text-3xl font-bold text-white leading-tight mt-1">
            Banca Técnica — faça seu login:
          </p>
        </div>

        <div className="login-card">
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} noValidate>
              <div className="login-field">
                <label className="login-label">Email</label>
                <input
                  type="email"
                  className="login-input"
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  disabled={loading}
                  autoFocus
                />
              </div>
              {errorMessage && <p className="login-error">{errorMessage}</p>}
              <div className="login-actions-right">
                <button type="submit" className="login-btn-submit" disabled={loading}>
                  {loading ? 'ENVIANDO...' : 'ENVIAR'}
                </button>
              </div>
            </form>
          )}

          {step === 'register' && (
            <form onSubmit={handleRegisterSubmit} noValidate>
              <div className="login-field">
                <label className="login-label">Nome completo</label>
                <p className="login-readonly-value">{validatedUser?.name}</p>
              </div>
              <div className="login-field">
                <label className="login-label">Email</label>
                <p className="login-readonly-value">{validatedUser?.email}</p>
              </div>
              <div className="login-field">
                <label className="login-label">Crie uma senha</label>
                <input
                  type="password"
                  className="login-input"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="login-field">
                <label className="login-label">Confirmar senha</label>
                <input
                  type="password"
                  className="login-input"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="otp-section">
                <p className="otp-message">Um código de 5 dígitos foi enviado para o seu email</p>
                <div className="otp-boxes">
                  {otpDigits.map((digit, i) => (
                    <input
                      key={i}
                      ref={el => { otpRefs.current[i] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      className="otp-box"
                      value={digit}
                      onChange={e => handleOtpInput(i, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(i, e)}
                      onPaste={e => handleOtpPaste(i, e)}
                      disabled={loading}
                    />
                  ))}
                </div>
                <p className="otp-resend">
                  Não recebeu?{' '}
                  {canResend ? (
                    <button type="button" onClick={handleResendOtp} className="otp-resend-link">
                      Reenviar código
                    </button>
                  ) : (
                    <span className="otp-resend-link">
                      Reenviar código em {formatCountdown(otpCountdown)}
                    </span>
                  )}
                </p>
              </div>
              {errorMessage && <p className="login-error text-center">{errorMessage}</p>}
              <div className="login-actions-center">
                <button type="submit" className="login-btn-submit" disabled={loading}>
                  {loading ? 'CADASTRANDO...' : 'CADASTRAR'}
                </button>
              </div>
            </form>
          )}

          {step === 'login' && (
            <form onSubmit={handleLoginSubmit} noValidate>
              <div className="login-field">
                <label className="login-label">Login</label>
                <input
                  type="text"
                  className="login-input"
                  value={loginInput}
                  onChange={e => setLoginInput(e.target.value)}
                  disabled={loading}
                  autoFocus
                />
              </div>
              <div className="login-field">
                <label className="login-label">Senha</label>
                <input
                  type="password"
                  className="login-input"
                  value={loginPassword}
                  onChange={e => setLoginPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
              {errorMessage && <p className="login-error">{errorMessage}</p>}
              <p className="otp-resend">
                <button
                  type="button"
                  onClick={handleForgotPasswordBanca}
                  className="otp-resend-link"
                  disabled={loading}
                >
                  Esqueci minha senha
                </button>
              </p>
              <div className="login-actions-right">
                <button type="submit" className="login-btn-submit" disabled={loading}>
                  {loading ? 'ENTRANDO...' : 'ENTRAR'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
